import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Row,
  Select,
} from "antd";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../app/store";
import { useAppSelector } from "../../app/hooks";
import ComponentPlacesAutocomplete from "../map/PlaceAutocomplete";
import { DirectionType } from "../../shared/constants/DirectionType";
import { resetTrip } from "../../features/map/mapSlice";
import { getAllAdmins } from "../../services/adminApi";
import { useServicesTypes } from "../common/hooks/useServicesTypes";
import {
  estimateSimulationFare,
  parseInternationalPricingError,
} from "../../services/internationalPricingApi";
import {
  FareSimulationResponse,
  TripType,
} from "../../shared/dtos/internationalPricingDTO";
import { buildSimulationRequest } from "./utils";
import SimulationResult from "./SimulationResult";

interface SimulationFormValues {
  adminId: number;
  serviceTypeId: number;
  tripType: TripType;
  quantity: number;
  estimatedKm: number;
}

const FareSimulationTab = () => {
  const [form] = Form.useForm<SimulationFormValues>();
  const dispatch = useDispatch<AppDispatch>();
  const destinations = useAppSelector(
    (state: RootState) => state.map.destinations,
  );
  const serviceTypes = useServicesTypes();
  const [admins, setAdmins] = useState<Array<{ value: number; label: string }>>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [distanceLoading, setDistanceLoading] = useState(false);
  const [distanceError, setDistanceError] = useState<string>();
  const [error, setError] = useState<string>();
  const [result, setResult] = useState<FareSimulationResponse>();

  useEffect(() => {
    dispatch(resetTrip());
    getAllAdmins().then((items) =>
      setAdmins(
        items.map((admin: any) => ({
          value: admin.id,
          label: admin.name,
        })),
      ),
    );
    return () => {
      dispatch(resetTrip());
    };
  }, [dispatch]);

  useEffect(() => {
    const origin = destinations?.pickUp;
    const destination = destinations?.dropOff;
    if (!origin || !destination) {
      form.setFieldValue("estimatedKm", undefined);
      setDistanceError(undefined);
      return;
    }

    if (typeof google === "undefined" || !google.maps?.DirectionsService) {
      setDistanceError("Google Maps no está disponible para calcular la ruta.");
      return;
    }

    let active = true;
    setDistanceLoading(true);
    setDistanceError(undefined);
    form.setFieldValue("estimatedKm", undefined);

    const directionsService = new google.maps.DirectionsService();
    directionsService.route(
      {
        origin: {
          lat: origin.originLatitude,
          lng: origin.originLongitude,
        },
        destination: {
          lat: destination.destinationLatitude,
          lng: destination.destinationLongitude,
        },
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (response, status) => {
        if (!active) return;
        setDistanceLoading(false);
        if (status !== google.maps.DirectionsStatus.OK || !response) {
          setDistanceError("No se pudo calcular la distancia de la ruta.");
          return;
        }

        const meters = response.routes[0]?.legs.reduce(
          (total, leg) => total + (leg.distance?.value || 0),
          0,
        );
        if (!meters) {
          setDistanceError("Google Maps no devolvió una distancia válida.");
          return;
        }
        form.setFieldValue("estimatedKm", Math.round(meters / 10) / 100);
      },
    );

    return () => {
      active = false;
    };
  }, [
    destinations?.dropOff,
    destinations?.pickUp,
    form,
  ]);

  const submit = async (values: SimulationFormValues) => {
    const origin = destinations?.pickUp;
    const destination = destinations?.dropOff;
    if (!origin || !destination) {
      setError("Selecciona un origen y un destino de las sugerencias.");
      return;
    }

    const payload = buildSimulationRequest(values, origin, destination);

    setLoading(true);
    setError(undefined);
    setResult(undefined);
    try {
      setResult(await estimateSimulationFare(payload));
    } catch (simulationError) {
      const parsed = parseInternationalPricingError(simulationError);
      setError(
        parsed.status === 404
          ? "El simulador de tarifas no está habilitado en este ambiente."
          : parsed.message,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="international-pricing-tab">
      <Card>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ tripType: "one_way", quantity: 1 }}
          onFinish={submit}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="adminId"
                label="Empresa que solicita"
                rules={[{ required: true, message: "Selecciona una empresa." }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={admins}
                  placeholder="Seleccionar empresa"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="tripType"
                label="Tipo de trayecto"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { value: "one_way", label: "Solo ida" },
                    { value: "round_trip", label: "Ida y vuelta" },
                    { value: "hourly", label: "Por hora" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="serviceTypeId"
                label="Tipo de servicio"
                rules={[{ required: true, message: "Selecciona un servicio." }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Seleccionar servicio"
                  options={serviceTypes
                    .filter((service) => service.id != null)
                    .map((service) => ({
                      value: service.id as number,
                      label: service.name,
                    }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="quantity"
                label="Vehículos"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Dirección de origen" required>
                <ComponentPlacesAutocomplete
                  type={DirectionType.Origin}
                  placeholder="Seleccionar origen"
                  worldwide
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Dirección de destino" required>
                <ComponentPlacesAutocomplete
                  type={DirectionType.Destination}
                  placeholder="Seleccionar destino"
                  worldwide
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="estimatedKm"
                label="KM estimados"
                rules={[{ required: true, message: "Selecciona ambas direcciones." }]}
              >
                <InputNumber
                  disabled
                  placeholder={
                    distanceLoading ? "Calculando ruta..." : "Distancia de Google Maps"
                  }
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
          {distanceError && (
            <Alert
              type="warning"
              showIcon
              message={distanceError}
              style={{ marginBottom: 16 }}
            />
          )}
          <Button type="primary" htmlType="submit" loading={loading}>
            Calcular tarifa
          </Button>
        </Form>
      </Card>

      {error && (
        <Alert
          type="error"
          showIcon
          closable
          message={error}
          onClose={() => setError(undefined)}
        />
      )}

      {result && <SimulationResult result={result} />}
    </div>
  );
};

export default FareSimulationTab;
